import React from 'react';
import styled from 'styled-components';
import { IoChevronDown } from 'react-icons/io5';
import { devices } from '../../data/constants';

const AccordionContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.cardBG};
  box-shadow: ${({ $isExpanded }) => $isExpanded ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none'};
  transition: box-shadow 0.3s ease;
`;

const AccordionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  cursor: pointer;
  background: ${({ $isExpanded, theme }) => $isExpanded ? theme.colors.opacityColor : 'transparent'};
  border-bottom: ${({ $isExpanded, theme }) => $isExpanded ? `1px solid ${theme.colors.borderColor}` : 'none'};
  transition: background 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.opacityColor};
  }

  @media only screen and ${devices.md} {
    padding: 14px 16px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StepBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 14px;
  font-weight: 600;
  background: ${({ $isCompleted, $isExpanded, theme }) => 
    $isCompleted ? theme.colors.activeColor : 
    $isExpanded ? theme.colors.activeColor : 
    theme.colors.borderColor};
  color: ${({ $isCompleted, $isExpanded, theme }) => 
    $isCompleted || $isExpanded ? '#fff' : theme.colors.cardText};
  transition: all 0.3s ease;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.cardText};

  @media only screen and ${devices.md} {
    font-size: 14px;
  }
`;

const SummaryText = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.subTextColor};
  margin-left: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;

  @media only screen and ${devices.md} {
    display: none;
  }
`;

const SummaryTextMobile = styled.div`
  display: none;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.subTextColor};
  padding: 0 16px 10px 56px;

  @media only screen and ${devices.md} {
    display: ${({ $show }) => $show ? 'block' : 'none'};
  }
`;

const ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.subTextColor};
  transform: rotate(${({ $isExpanded }) => $isExpanded ? '0deg' : '-90deg'});
  transition: transform 0.3s ease;
`;

const AccordionContent = styled.div`
  max-height: ${({ $isExpanded }) => $isExpanded ? '5000px' : '0'};
  opacity: ${({ $isExpanded }) => $isExpanded ? 1 : 0};
  overflow: hidden;
  transition: max-height 0.4s ease, opacity 0.3s ease;
`;

const ContentInner = styled.div`
  padding: 20px;

  @media only screen and ${devices.md} {
    padding: 16px;
  }
`;

const AccordionSection = ({ 
  stepNumber, 
  title, 
  summary, 
  isExpanded, 
  isCompleted,
  onToggle, 
  children 
}) => {
  return (
    <AccordionContainer $isExpanded={isExpanded}>
      <AccordionHeader $isExpanded={isExpanded} onClick={onToggle}>
        <HeaderLeft>
          <StepBadge $isCompleted={isCompleted} $isExpanded={isExpanded}>
            {stepNumber}
          </StepBadge>
          <SectionTitle>{title}</SectionTitle>
          {!isExpanded && summary && <SummaryText>— {summary}</SummaryText>}
        </HeaderLeft>
        <ChevronIcon $isExpanded={isExpanded}>
          <IoChevronDown size={20} />
        </ChevronIcon>
      </AccordionHeader>
      {!isExpanded && summary && (
        <SummaryTextMobile $show={!isExpanded}>{summary}</SummaryTextMobile>
      )}
      <AccordionContent $isExpanded={isExpanded}>
        <ContentInner>
          {children}
        </ContentInner>
      </AccordionContent>
    </AccordionContainer>
  );
};

export default AccordionSection;
